import { ImageUrl } from '@utils'
import { isEmpty, numericalSorter } from './utils'

export enum BaseType {
    WIDTH = 'WIDTH',
    HEIGHT = 'HEIGHT'
}

export enum SrcType {
    gif = 'gif',
    png = 'png',
    webp = 'webp',
    pkmz = 'pkmz'
}

export type MixedUnit = string | number
export type Size = { [mediaQuery: string]: MixedUnit }
export type SrcSize = { [mediaQuery: string]: number }
export type SrcSizes = SrcSize[]
export type Sizes = Size[]


const defaultResolutions = [1.5, 2]

type Breakpoints = { [screenSize: string]: string }
const breakpoints: Breakpoints = {
    phone: '20em',
    phablet: '28em',
    'phone-landscape': '47em',
    tablet: '55em',
    'tablet-landscape': '65em',
    desktop: '90em',
    largeScreen: '118em',
}

const breakpointsRegEx = RegExp(`(${Object.keys(breakpoints).join('|')})$`)

const getMediaQueryString = (mq: string) => (
    mq.replace(breakpointsRegEx, (match: string) => `width: ${breakpoints[match]}`)
)

const widthsForAllResolutions = (widths: number[], resolutions: number[] = defaultResolutions) => {
    const allWidths = resolutions.reduce((all, res) => {
        const resWidths = widths.map(w => Math.floor(w * res))
        return [...all, ...resWidths]
    }, widths)

    return Array.from(new Set(allWidths)).sort(numericalSorter) // distinct sorted integers
}


/**
 * defaultToPx(param)
 *
 * @param number | string
 *
 * @use defaultToPx(300) // 300px
 * @use defaultToPx('300vw') // 300vw
 */
export const defaultToPx = (value: MixedUnit) => (
    typeof value === 'string' ? value : `${value}px`
)


/**
 * getSameSizeSrcSet('img.jpg', 300, 'webp')
 *
 * @returns "img._SX300_FMwebp_.jpg 300w, img._SX600_FMwebp_.jpg 600w"
 */
const getSameSizeSrcSet = (src: string, baseValue: number, baseType: BaseType, type?: SrcType, skipStripTags?: boolean) => {
    const srcSets = defaultResolutions.map((resolution) => {
        const value = baseValue * resolution
        return `${formatImageUrl({src, value, baseType, type, skipStripTags})} ${resolution}x`
    })

    srcSets && srcSets.unshift(formatImageUrl({ src, value: baseValue, baseType, type, skipStripTags }))

    return srcSets && srcSets.join(', ')
}


type FormatImageUrl = { src: string, value: number, baseType: BaseType, type?: SrcType, skipStripTags?: boolean }
const formatImageUrl = ({ src, value, baseType, type, skipStripTags }: FormatImageUrl) => {
    if (!src) {
        return ''
    }

    const baseImgUrl = new ImageUrl(src)

    if (!skipStripTags) {
        baseImgUrl.stripTags()
    }

    if (baseType === BaseType.HEIGHT) {
        baseImgUrl.scaleHeight(value)
    } else {
        baseImgUrl.scaleWidth(value)
    }

    if (type) {
        baseImgUrl.forceFormat(type)
    }

    return baseImgUrl.getImageUrl()
}


/**
 * getSrcSetForSizes('img.jpg', 300, {'min-desktop': 600})
 *
 * @returns "img.__SX300__.jpg 300w, img.__SX600__.jpg 600w"
 */
const getSrcSetForSizes = (src: string, widths: number[], baseType: BaseType, type?: SrcType, skipStripTags?: boolean) => {
    const srcSets = widths.map((value: number) => {
        return `${formatImageUrl({src, value, baseType, type, skipStripTags})} ${value}w`
    })

    return srcSets.join(', ')
}


/**
 * getSizes()
 *
 * @param defaultWidth: number | string // 300 / 300px / 100vw
 * @param sizes: Sizes
 * @use sizes(800, [{'max-width: 480px': 300}, {'min-desktop': 500}])
 * @returns "(max-width: 480px) 300px, (min-width: 90em) 500px,  800px"
 */
export const getSizes = (defaultWidth: MixedUnit, sizes: Sizes = []) => {
    const sizesAttr = sizes.reduce((sizesAttr: Array<string>, size: Size) => {
        const [mq, value] = Object.entries(size)[0]
        const mqString = getMediaQueryString(mq)

        if (mqString && value) {
            sizesAttr.push(`(${mqString}) ${defaultToPx(value)}`)
        }

        return sizesAttr
    }, [])

    sizesAttr.push(defaultToPx(defaultWidth))

    return sizesAttr.join(', ')
}


/**
 * responsiveAttrs({src: 'img.jpg', baseValue: 100, srcSizes: [{'min-desktop': 200}], [1.5, 2]})
 *
 * @returns "img.__SX100__.jpg 100w, img.__SX200__.jpg 200w, img.__SX300__.jpg 300w, img.__SX400__.jpg 400w"
 */

type ResponsiveAttrs = {
    src: string
    baseValue: number
    baseType?: BaseType
    srcSizes?: SrcSizes
    format?: SrcType
    resolutions?: number[]
    skipStripTags?: boolean
}
export const responsiveAttrs = ({ src, baseValue, baseType = BaseType.WIDTH, srcSizes, format, resolutions, skipStripTags }: ResponsiveAttrs) => {


    if (baseType === BaseType.HEIGHT && srcSizes) {
        throw new Error("BaseType.HEIGHT can't be used with srcSizes. It is only compatible with format and resolutions.")
    }

    // same img size on all screen sizes (but different resolutions)
    if (isEmpty(srcSizes)) {
        return {
            src: formatImageUrl({ src, value: baseValue, baseType, type: format, skipStripTags }),
            srcSet: getSameSizeSrcSet(src, baseValue, baseType, format, skipStripTags)
        }
    }

    // different img size on different screen sizes
    const widths = srcSizes!.map((srcSize) => Object.values(srcSize)[0])

    widths.push(baseValue)

    const allWidths = widthsForAllResolutions(widths, resolutions)

    return {
        src: formatImageUrl({src, value: baseValue, baseType, type: format, skipStripTags}),
        srcSet: getSrcSetForSizes(src, allWidths, baseType, format, skipStripTags),
        sizes: getSizes(baseValue, srcSizes!)
    }
}